import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Collapse, Grid, Tooltip, Button } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';

import { Switch, Typography } from '../shared';
import { SET_SELECTED_ITEM } from '../store/actions';
import { useThemeToggle } from '../hooks/useThemeToggle';
import InviteUserLinkModal from 'components/Rewardful/InviteUserLinkModal';
import { ReactComponent as CirclerLogo } from '../assets/icons/circler-logo.svg';
import { ReactComponent as DarkCirclerLogo } from '../assets/icons/DarkLogo.svg';
import { MOBILE_MENU_OPTIONS, SIDE_MENU_OPTIONS } from '../components/SideMenuOptions';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../hooks/useAuth';

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    margin: theme.spacing(0, 0),
    padding: theme.spacing(3),
    backgroundImage: 'unset',
    ...theme.mixins.toolbar,
}));

const transactionItems = [
    {
        name: 'Import bank transactions',
        path: '/import-bank-transactions',
    },

    {
        name: 'Upload Transactions',
        path: '/upload-transactions',
    },
];

const inventoryItems = [
    {
        name: 'Purchases',
        path: '/purchases',
    },
    {
        name: 'Emails',
        path: '/inventory-emails',
    },
    {
        name: 'Inventory',
        path: '/inventory',
    },
];

const inventoryEmails = [
    {
        name: 'Emails',
        path: 'inventory-emails/emails',
    },
    {
        name: 'Inventory',
        path: 'inventory-emails/inventory-tab',
    },
];

const invoice = [
    {
        name: 'Payments',
        path: '/invoices/payments',
    },
    {
        name: 'Credentials',
        path: '/invoices/credentials',
    },
    {
        name: 'Watchlist',
        path: '/invoices/watchlist',
    },
    {
        name: 'Payments Upload',
        path: '/invoices/payments-file',
    },
    {
        name: 'Invoices Upload',
        path: '/invoices/invoices-file',
    },
];

function Sidebar({ isDrawerOpen, currentPath, handleMenuClick, isScreenLowerThan1024 }) {
    // const { user } = useAuth();

    const { themeMode, toggleTheme } = useThemeToggle();
    const navigate = useNavigate();
    const companyData = useSelector((state) => state.company.data);
    const hasInvoiceAccess = companyData?.is_invoice_reconciler;
    const hasPurchaseAccess = companyData?.is_purchase_reconciler;
    const sidebarMenus = useMemo(() => {
        let menus = isScreenLowerThan1024
            ? [...SIDE_MENU_OPTIONS, ...MOBILE_MENU_OPTIONS]
            : [...SIDE_MENU_OPTIONS];

        return menus;
    }, [isScreenLowerThan1024]);

    const location = useLocation();
    const dispatch = useDispatch();

    // const selectedItem = useSelector((state) => state.me.selectedItems);

    const [open, setOpen] = useState(null);
    const [showInviteLink, setShowInviteLink] = useState(false);

    useEffect(() => {
        const item = [
            ...transactionItems,
            ...inventoryItems,
            ...inventoryItems,
            ...inventoryEmails,
            ...invoice,
        ].find((item) => item.path === location.pathname);

        if (item) {
            dispatch(SET_SELECTED_ITEM(item.name));
        } else {
        }
    }, [location.pathname, dispatch]);

    useEffect(() => {
        const activeParentMenu = sidebarMenus.find((option) => {
            const directMatch = option.items?.some(
                (item) =>
                    location.pathname === item.path ||
                    location.pathname.startsWith(item.path + '/') ||
                    item.items?.some(
                        (nestedItem) =>
                            location.pathname === nestedItem.path ||
                            location.pathname.startsWith(nestedItem.path + '/'),
                    ),
            );

            if (directMatch) {
                return true;
            }

            return (
                option.path &&
                (location.pathname === option.path ||
                    location.pathname.startsWith(option.path + '/'))
            );
        });

        if (activeParentMenu) {
            setOpen(activeParentMenu.name);
        }
    }, [location.pathname, sidebarMenus]);

    const handleMenuClicks = (e, option) => {
        dispatch(SET_SELECTED_ITEM(option.name));

        if (option.isCustomAction) {
            if (option.name === 'Invite') {
                setShowInviteLink(true);

                return;
            }
        }

        // Check if the option is locked
        const isPurchaseLocked =
            (option.name === 'Purchases' ||
                option.name === 'Transaction' ||
                option.name === 'Emails') &&
            option.locked &&
            !hasPurchaseAccess;
        const isInvoiceLocked = option.name === 'Invoices' && option.locked && !hasInvoiceAccess;

        if (isPurchaseLocked || isInvoiceLocked) {
            // Prevent navigation for locked items
            e?.preventDefault();
            e?.stopPropagation();
            return;
        }

        handleMenuClick(e, option);
        setOpen((currentOpen) => (currentOpen === option.name ? null : option.name));
    };

    const handleCirclerLogoClick = () => {
        navigate('/purchases/for-review');
        dispatch(SET_SELECTED_ITEM('Inventory Reconciliation'));
    };

    const handleItemClick = (itemName, itemObj) => {
        dispatch(SET_SELECTED_ITEM(itemName));

        if (itemObj && itemObj.isCustomAction) {
            if (itemName === 'Invite') {
                setShowInviteLink(true);
                return;
            }
        }

        // Check if parent menu is locked
        const parentMenu = sidebarMenus.find((menu) =>
            menu.items?.some((item) => item.name === itemName),
        );
        if (parentMenu) {
            const isPurchaseLocked =
                (parentMenu.name === 'Purchases' ||
                    parentMenu.name === 'Transaction' ||
                    parentMenu.name === 'Emails') &&
                parentMenu.locked &&
                !hasPurchaseAccess;
            const isInvoiceLocked =
                parentMenu.name === 'Invoices' && parentMenu.locked && !hasInvoiceAccess;

            if (isPurchaseLocked || isInvoiceLocked) {
                // Prevent navigation for locked items
                return;
            }
        }

        // If the item has a path, navigate to it
        if (itemObj?.path) {
            navigate(itemObj.path);
            return;
        }

        // Fallback to the old behavior for backward compatibility
        const item = [
            ...transactionItems,
            ...inventoryItems,
            ...inventoryItems,
            ...inventoryEmails,
            ...invoice,
        ].find((item) => item.name === itemName);

        if (item) {
            navigate(item.path);
        }

        if (['History', 'Home']?.includes(currentPath)) {
            handleMenuClick(null, {
                name: itemName,
                icon: {},
                selectedIcon: {},
                darkMode: {},
                path: item?.path || '',
                displayName: 'Transaction',
                collapse: true,
                items: transactionItems,
            });
        }
    };

    return (
        <>
            <DrawerHeader>
                <Grid container>
                    <Box
                        sx={{
                            cursor: 'pointer',
                            p: '0 !important',
                        }}
                    >
                        {themeMode === 'light' ? (
                            <CirclerLogo width={150} height={40} onClick={handleCirclerLogoClick} />
                        ) : (
                            <DarkCirclerLogo
                                width={150}
                                height={40}
                                a
                                onClick={handleCirclerLogoClick}
                            />
                        )}
                    </Box>
                </Grid>
            </DrawerHeader>

            <List sx={{ top: isScreenLowerThan1024 ? 0 : 5 }}>
                {sidebarMenus.map((option, index) => {
                    let isActive = false;
                    if (option?.items) {
                        isActive = option.items.some((item) => item.path === location.pathname);
                    } else {
                        isActive = option.path === location.pathname;
                    }
                    // Determine if the option is locked based on its type
                    const isPurchaseLocked =
                        (option.name === 'Purchases' ||
                            option.name === 'Transaction' ||
                            option.name === 'Emails') &&
                        option.locked &&
                        !hasPurchaseAccess;
                    const isInvoiceLocked =
                        option.name === 'Invoices' && option.locked && !hasInvoiceAccess;
                    const isLocked = isPurchaseLocked || isInvoiceLocked;
                    const Icon = isActive
                        ? themeMode === 'dark'
                            ? option.darkMode
                            : option.selectedIcon
                        : option.icon;

                    return (
                        <React.Fragment key={index}>
                            <Box width={'100%'}></Box>

                            <ListItem
                                key={index}
                                disablePadding
                                sx={{
                                    p: '8px 0',
                                }}
                            >
                                <Box className={isActive ? 'active' : ''} />
                                <ListItemButton
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: isDrawerOpen ? 'initial' : 'center',
                                        px: isActive ? 2.25 : 2.5,
                                        '& > .MuiListItemButton-root:hover': {
                                            backgroundColor: 'unset',
                                        },
                                    }}
                                    onClick={(e) => handleMenuClicks(e, option)}
                                >
                                    {isLocked ? (
                                        <Tooltip
                                            title={
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        textAlign: 'center',
                                                        zIndex: 9999,
                                                        width: isDrawerOpen
                                                            ? 'calc(100% - 32px)'
                                                            : 'auto',
                                                        maxWidth: isDrawerOpen ? 'none' : '200px',
                                                        margin: isDrawerOpen ? '0 16px' : '0',
                                                        boxSizing: 'border-box',
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <Typography
                                                        mb={2}
                                                        text={
                                                            isInvoiceLocked
                                                                ? 'Upgrade to Invoice Reconciler'
                                                                : 'Upgrade to Purchase Reconciler'
                                                        }
                                                        customStyles={{
                                                            fontSize: 14,
                                                            fontWeight: 500,
                                                            color: (theme) =>
                                                                theme.palette.mode === 'dark'
                                                                    ? '#F9FAFB'
                                                                    : 'black',
                                                        }}
                                                    />

                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            navigate('/subscription-part-two');
                                                        }}
                                                        sx={{
                                                            backgroundColor: '#0A8C4D',
                                                            '&:hover': {
                                                                backgroundColor: '#07713D',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow:
                                                                    '0 2px 4px rgba(0,0,0,0.2)',
                                                            },
                                                            textTransform: 'none',
                                                            fontSize: '0.85rem',
                                                            padding: '4px 16px',
                                                            fontWeight: 500,
                                                            borderRadius: '4px',
                                                            transition: 'all 0.2s',
                                                        }}
                                                    >
                                                        Upgrade Now
                                                    </Button>
                                                </Box>
                                            }
                                            placement="right"
                                            arrow
                                            disableFocusListener={false}
                                            disableHoverListener={false}
                                            disableTouchListener={false}
                                            slotProps={{
                                                popper: {
                                                    modifiers: [
                                                        {
                                                            name: 'offset',
                                                            options: {
                                                                offset: [0, 14],
                                                            },
                                                        },
                                                    ],
                                                },
                                            }}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: (theme) => ({
                                                        backgroundColor:
                                                            theme.palette.background.paper,
                                                        color: theme.palette.text.primary,
                                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                                        borderRadius: '8px',
                                                        maxWidth: isDrawerOpen ? 'none' : '200px',
                                                        width: isDrawerOpen ? '100%' : 'auto',
                                                        zIndex: 9999,
                                                        '& .MuiTooltip-arrow': {
                                                            color: theme.palette.background.paper,
                                                        },
                                                    }),
                                                },
                                            }}
                                        >
                                            <Box sx={{ position: 'relative' }}>
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        mr: isDrawerOpen ? 3 : 'auto',
                                                        justifyContent: 'center',
                                                        position: 'relative',
                                                        '&:hover .lock-icon-overlay': {
                                                            opacity: 1,
                                                        },
                                                    }}
                                                >
                                                    {Icon && (
                                                        <Box
                                                            className={
                                                                isLocked
                                                                    ? 'locked-icon-wrapper'
                                                                    : ''
                                                            }
                                                            sx={{
                                                                position: 'relative',
                                                                width: '24px',
                                                                height: '24px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                // '&::before': {
                                                                //     content: '""',
                                                                //     position: 'absolute',
                                                                //     top: '-15px',
                                                                //     left: '-20px',
                                                                //     width: '40px',
                                                                //     height: '40px',
                                                                //     backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolygon points='50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35' fill='none' stroke='%23FFD700' stroke-width='7'/%3E%3C/svg%3E")`,
                                                                //     backgroundSize: 'contain',
                                                                //     backgroundRepeat: 'no-repeat',
                                                                //     zIndex: 2,
                                                                //     transform: 'scale(0.8)',
                                                                // },
                                                                '& > *': {
                                                                    position: 'relative',
                                                                    zIndex: 2,
                                                                },
                                                                '& svg': {
                                                                    '& path': {
                                                                        stroke: isLocked
                                                                            ? '#FFD700 !important'
                                                                            : 'inherit',
                                                                        fill: isLocked
                                                                            ? 'none !important'
                                                                            : 'inherit',
                                                                    },
                                                                },
                                                                '&.locked-icon-wrapper svg path': {
                                                                    stroke: '#FFD700 !important',
                                                                    fill: 'none !important',
                                                                },
                                                                // More specific selector to ensure override
                                                                '&.locked-icon-wrapper > svg > path':
                                                                {
                                                                    stroke: '#FFD700 !important',
                                                                    fill: 'none !important',
                                                                },
                                                            }}
                                                        >
                                                            <Icon />
                                                        </Box>
                                                    )}
                                                    <Box
                                                        className="lock-icon-overlay"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -10,
                                                            right: isDrawerOpen ? 8 : -4,
                                                            left: 'auto',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: '2px',
                                                            zIndex: 2,
                                                            '& svg': {
                                                                transition: 'all 0.2s',
                                                            },
                                                            '&:hover svg': {
                                                                opacity: 1,
                                                                transform: 'scale(1.2)',
                                                            },
                                                        }}
                                                    >
                                                        <LockOutlinedIcon
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: -50,
                                                                left: -10,
                                                                fontSize: '20px',
                                                                color: 'white',
                                                                backgroundColor:
                                                                    'rgba(0, 0, 0, 0.8)',
                                                                borderRadius: '2px',
                                                                padding: '2px',
                                                                opacity: 0.9,
                                                                zIndex: 3,
                                                            }}
                                                        />
                                                    </Box>
                                                </ListItemIcon>
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Box sx={{ position: 'relative' }}>
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: isDrawerOpen ? 3 : 'auto',
                                                    justifyContent: 'center',
                                                    position: 'relative',
                                                }}
                                            >
                                                {Icon && <Icon />}
                                            </ListItemIcon>
                                        </Box>
                                    )}
                                    <ListItemText
                                        sx={{
                                            opacity: isDrawerOpen ? 1 : 0,
                                        }}
                                    >
                                        <Grid
                                            container
                                            justifyContent={'space-between'}
                                            sx={{
                                                width: isActive ? '138px' : '100%',
                                            }}
                                        >
                                            <Grid item>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography
                                                        text={option.name}
                                                        customStyles={{
                                                            color: isActive ? '#0061DB' : 'inherit',
                                                            fontWeight: isActive
                                                                ? 600
                                                                : isScreenLowerThan1024 &&
                                                                    index >= 5
                                                                    ? 500
                                                                    : 400,
                                                            fontSize:
                                                                isScreenLowerThan1024 && index >= 5
                                                                    ? '14px'
                                                                    : '16px',
                                                        }}
                                                    />
                                                    {isLocked && (
                                                        <LockOutlinedIcon
                                                            sx={{
                                                                fontSize: '16px',
                                                                color: isActive
                                                                    ? '#0061DB'
                                                                    : (theme) =>
                                                                        theme.textColor.primary,
                                                                opacity: 0.7,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </Grid>
                                            {option.hasOwnProperty('notificationCount') ? (
                                                <Grid
                                                    item
                                                    sx={{
                                                        display: isDrawerOpen ? 'block' : 'none',
                                                    }}
                                                ></Grid>
                                            ) : option.hasOwnProperty('showSwitch') ? (
                                                <Grid item display={'flex'} alignItems={'center'}>
                                                    <Switch
                                                        checked={
                                                            themeMode === 'dark' ? true : false
                                                        }
                                                        onClick={() => toggleTheme()}
                                                    />
                                                </Grid>
                                            ) : (
                                                <></>
                                            )}
                                        </Grid>
                                    </ListItemText>
                                </ListItemButton>
                            </ListItem>
                            {option.items && (
                                <Collapse
                                    in={open === option.name && isDrawerOpen}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <List
                                        component="div"
                                        disablePadding
                                        sx={{ marginLeft: '39px' }}
                                    >
                                        {option.items.map((item) => (
                                            <ListItemButton
                                                key={item.name}
                                                sx={{
                                                    pl: 4,
                                                    color:
                                                        location.pathname === item.path
                                                            ? '#0061DB'
                                                            : '#6C727F',
                                                }}
                                                onClick={() => handleItemClick(item.name, item)}
                                            >
                                                <ListItemText primary={item.name} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    );
                })}
            </List>

            {showInviteLink && (
                <InviteUserLinkModal
                    open={showInviteLink}
                    companyId={companyData?.id}
                    handleCloseModal={() => setShowInviteLink(false)}
                    handleExclusionRuleCreationSuccess={() => {
                        setShowInviteLink(false);
                    }}
                />
            )}
        </>
    );
}

export default Sidebar;
